

































import {Component, Prop, Vue} from 'vue-property-decorator';

import {debounce, getResolution, isRetina} from '@/utils';
import {ImageSourceType} from '@/components/types';

@Component({})
export default class LazyImage extends Vue {
	@Prop({default: null}) readonly variant?: string | null;

	@Prop({required: true}) readonly alt: string;
	@Prop({required: true}) readonly tiny: ImageSourceType;

	@Prop({default: false}) readonly forceload: boolean; // set to false for observer to work
	@Prop({default: true}) readonly observe: boolean;
	@Prop({default: false}) readonly fixedBackground: boolean;
	@Prop({default: () => ({})}) readonly medium: ImageSourceType;
	@Prop({default: () => ({})}) readonly large: ImageSourceType;
	@Prop({required: false, default: 16 / 9}) readonly aspectRatio: number | boolean;

	@Prop() readonly styles?: CSSStyleDeclaration;
	@Prop() readonly classes?: string[];

	readonly placeholder: string = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

	created: boolean = false;
	observed: boolean = false;
	loaded: boolean = false;
	firstLoad: boolean = false;
	source: string = this.placeholder;

	observer: IntersectionObserver | null = null;
	error: string | null = null;

	get classList(): (string | null)[] {
		return [
			'image',
			this.loaded ? `image--loaded` : null,
			this.variant ? `image--${this.variant}` : null,
			...(this.classes || [])
		];
	}

	get fixedStyles(): { background: string, height: string } {
		return {
			background: `url(${this.source}) no-repeat 50% fixed / cover`,
			height: '100%'
		};
	}

	get aspectRatioStyles(): { paddingBottom: string } | false {
		if (typeof this.aspectRatio === 'boolean') {
			return false;
		}
		return {
			paddingBottom: `${(100 / this.aspectRatio).toFixed(4)}%`
		};
	}

	mounted(): void {
		if (typeof window === 'undefined') {
			return;
		}

		if (this.forceload) {
			this.observed = true;

			this.load();
		}

		if (this.observe) {
			this.observer = new IntersectionObserver(this.observerHandler.bind(this), {
				rootMargin: '0px 0px 0px 0px'
			});

			debounce(this.observer.observe(this.$el));

			return;
		}
	}

	observerHandler(entries: IntersectionObserverEntry[], observer: IntersectionObserver | null = null): void {
		if (!entries) {
			this.observed = true;

			return;
		}

		const entry = entries.shift();

		if (!entry || !entry.isIntersecting) {
			return;
		}

		if (observer) {
			observer.unobserve(entry.target);
		}

		this.observed = true;

		this.load();
	}

	resizeEndHandler(event: Event | null = null): void {
		if (!event) {
			return;
		}

		if (this.loaded && this.observed) {
			this.load();
		}
	}

	load(): void {
		if (this.created) {
			return;
		}

		this.error = null;

		let source: string | null = null;

		const activeResolutions = getResolution()
			// @ts-ignore
			.filter((possibleResolution: string) => this[possibleResolution] !== null)
			.reverse();

		activeResolutions.forEach((activeResolution: string) => {
			if (source) {
				return;
			}

			// @ts-ignore
			if (typeof this[activeResolution] === 'object') {
				// @ts-ignore
				source = this[activeResolution][isRetina() ? 'retina' : 'default'];
			}
		});

		if (!source) {
			this.error = 'Missing image source';

			return;
		}
		this.source = source;
	}

	_onLoad(): void {
		if (typeof window === 'undefined') {
			return;
		}

		if (this.firstLoad) {
			return;
		}

		this.loaded = true;
		this.firstLoad = true;

		window.addEventListener('resize', debounce(this.resizeEndHandler.bind(this), 60));
	}

	_onError(error: Error): void {
		this.error = error.message;
	}
}
